import { useId } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card, CardHeader, cx, Field, Pill, Select } from '../ui';

/**
 * Strategy checklist on a trade plan (INT-TRD-01, INT-LIB-06). The picker sits on the new-plan screen; the plan
 * records the strategy version and the ticks. Unticked items are for the Risk Manager to see — they never block the
 * automatic risk check.
 */

interface UsableStrategy {
  strategyId: string;
  code: string;
  name: string;
  versionId: string;
  version: number;
  markets: string[];
  checklist: { id: string; text: string; required: boolean }[];
}

export interface StrategyAnswers {
  strategyVersionId: string;
  answers: { itemId: string; checked: boolean }[];
}

/** Saves the picker's value on a freshly created (draft) plan. */
export async function saveStrategyChecklist(planId: string, value: StrategyAnswers) {
  await api(`/plans/${planId}/strategy-checklist`, { method: 'PUT', body: value });
}

export function StrategyChecklistPicker({ marketCode, value, onChange }: { marketCode?: string; value: StrategyAnswers | null; onChange: (v: StrategyAnswers | null) => void }) {
  const groupId = useId();
  const { data } = useQuery({ queryKey: ['usable-strategies'], queryFn: () => api<UsableStrategy[]>('/library/strategies'), staleTime: 60_000 });
  const list = [...(data ?? [])].sort((a, b) => Number(b.markets.includes(marketCode ?? '')) - Number(a.markets.includes(marketCode ?? '')) || a.code.localeCompare(b.code));
  const chosen = list.find((s) => s.versionId === value?.strategyVersionId);
  const checked = (id: string) => !!value?.answers.find((a) => a.itemId === id)?.checked;
  const toggle = (id: string) => value && onChange({ ...value, answers: value.answers.map((a) => (a.itemId === id ? { ...a, checked: !a.checked } : a)) });
  if (data && !data.length) return null;
  const ticked = chosen ? chosen.checklist.filter((c) => checked(c.id)).length : 0;

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Field label="Strategy (optional)" className="min-w-0 flex-[1_1_260px]">
          <Select
            value={value?.strategyVersionId ?? ''}
            onChange={(e) => {
              const s = list.find((x) => x.versionId === e.target.value);
              onChange(s ? { strategyVersionId: s.versionId, answers: s.checklist.map((c) => ({ itemId: c.id, checked: false })) } : null);
            }}
          >
            <option value="">No strategy</option>
            {list.map((s) => (
              <option key={s.versionId} value={s.versionId}>
                {s.code} · {s.name} (v{s.version}){s.markets.length && marketCode && !s.markets.includes(marketCode) ? ` · ${s.markets.join('/')}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        {chosen && (
          <Link to={`/library/strategies/${encodeURIComponent(chosen.code)}`} target="_blank" className="pb-2.5 text-sm">
            Read the strategy ↗
          </Link>
        )}
      </div>
      {chosen && (
        <>
          <div role="group" aria-labelledby={groupId} className="flex flex-col">
            <span id={groupId} className="mb-1 text-xs text-muted">
              {chosen.code} pre-trade checklist · {ticked} of {chosen.checklist.length} ticked
            </span>
            {chosen.checklist.map((c) => (
              <label key={c.id} className="flex min-h-11 cursor-pointer items-center gap-3 border-t border-[#1a1f27] py-2 first:border-t-0">
                <input type="checkbox" className="h-5 w-5 shrink-0" checked={checked(c.id)} onChange={() => toggle(c.id)} />
                <span className={cx('flex-1', checked(c.id) ? 'text-ink' : 'text-ink2')}>{c.text}</span>
                {c.required && <Pill tone="warn">Must-have</Pill>}
              </label>
            ))}
          </div>
          <span className="text-xs text-muted">Unticked items are shown to the Risk Manager with your plan. They don't block the automatic risk check.</span>
        </>
      )}
    </Card>
  );
}

interface PlanChecklist {
  strategy: { id: string; code: string; name: string };
  version: { id: string; version: number };
  items: { id: string; text: string; required: boolean; checked: boolean }[];
  ticked: number;
  total: number;
  score: string;
  unticked: { id: string; text: string; required: boolean }[];
}

/** Read-only view on the plan page — for the trader and the Risk Manager. */
export function PlanStrategyChecklist({ planId }: { planId: string }) {
  const { data } = useQuery({ queryKey: ['plan', planId, 'strategy-checklist'], queryFn: () => api<{ checklist: PlanChecklist | null }>(`/plans/${planId}/strategy-checklist`) });
  const c = data?.checklist;
  if (!c) return null;
  const missedMust = c.unticked.some((u) => u.required);
  return (
    <Card tone={missedMust ? 'down' : c.unticked.length ? 'warn' : 'up'} className="mt-3.5">
      <CardHeader
        title={
          <>
            Strategy checklist ·{' '}
            <Link to={`/library/strategies/${encodeURIComponent(c.strategy.code)}?version=${c.version.version}`} className="font-mono">
              {c.strategy.code} v{c.version.version}
            </Link>
          </>
        }
        right={<Pill tone={c.unticked.length ? (missedMust ? 'down' : 'warn') : 'up'}>{c.ticked} of {c.total} ticked</Pill>}
      />
      <ul className="m-0 flex list-none flex-col p-0">
        {c.items.map((i) => (
          <li key={i.id} className="flex items-start gap-2.5 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0">
            <span aria-hidden="true" className={cx('w-4 font-bold', i.checked ? 'text-up' : i.required ? 'text-down' : 'text-warn')}>
              {i.checked ? '✓' : '✗'}
            </span>
            <span className={cx('flex-1', i.checked ? 'text-ink2' : 'text-ink')}>
              {i.text}
              {i.checked && <span className="sr-only"> — ticked</span>}
            </span>
            {!i.checked && <Pill tone={i.required ? 'down' : 'warn'}>{i.required ? 'Must-have not ticked' : 'Not ticked'}</Pill>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
